import { computed, Injectable } from '@angular/core';
import { AuthenticatedUser } from '@saexpeditions/sdk/authentication';
import { AuthStatus } from '@saexpeditions/sdk/shared';
import { StateService } from './state.service';

export interface IUserState {
  user: AuthenticatedUser | null;
  authStatus: AuthStatus;
}

/**
 * Servicio de estado del usuario.
 * Extiende StateService para logging automático y API consistente.
 * Expone computed de conveniencia para consumo directo en templates.
 */
@Injectable({
  providedIn: 'root',
})
export class UserStateService extends StateService<IUserState> {
  protected readonly stateName = 'UserState';

  constructor() {
    super({ user: null, authStatus: 'loading' });
  }

  // --- COMPUTED PÚBLICOS (solo lectura, atajos de conveniencia) ---
  readonly user = this.select('user');
  readonly status = this.select('authStatus');

  readonly isAuthenticated = computed(() => this.status() === 'authenticated');
  readonly isLoading = computed(() => this.status() === 'loading');

  readonly displayName = computed(() => this.user()?.name ?? '');
  readonly email = computed(() => this.user()?.email ?? '');
  readonly photoURL = computed(() => this.user()?.picture ?? '');
  readonly roles = computed(() => this.user()?.roles ?? []);
  readonly userId = computed(() => this.user()?.id ?? '');

  /** DMC/account label for the secure header. Falls back to the user display name. */
  readonly accountName = computed(() => this.attributeAsString('accountName') || this.displayName());

  /** Avatar/logo URL for the secure header. Prefers JWT attributes over Google picture. */
  readonly userLogoUrl = computed(() => this.attributeAsString('logoUrl') || this.photoURL());

  private attributeAsString(key: string): string {
    const value = this.user()?.attributes?.[key];
    return typeof value === 'string' && value.length > 0 ? value : '';
  }
}
